import {
  DAY_NAMES,
  DAY_SHORT,
  describeRanges,
  describeWeekly,
  hasOverridesInMonth,
  hasSchedule,
  monthDays,
  scheduleExceptionSchema,
  shiftMonthKey,
  timeRangeSchema,
  weekdayOf,
  weeklyFromHours,
  type AdvisorScheduleDto,
  type ScheduleCell,
  type ScheduleException,
  type WeeklySchedule,
} from '@performance-crm/shared';
import { CalendarClock, CalendarDays, ChevronLeft, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/toast-context';
import { fmt, todayInTz } from '@/lib/format';
import { currentMonth, monthLabel } from '@/lib/months';
import { useCompanyTimezone } from '@/lib/useCompanyTimezone';
import {
  useCatalogs,
  useClearScheduleMonth,
  useCopyScheduleMonth,
  useScheduleMonth,
  useSchedules,
  useSetScheduleDays,
  useUpsertSchedule,
} from '@/lib/queries';

const ORDER = [1, 2, 3, 4, 5, 6, 0];
const ROLE: Record<string, string> = {
  ASESOR: 'Asesor',
  COORDINADOR: 'Coordinador',
  JEFE_COMERCIAL: 'Jefe comercial',
};

/** Equipo → Horarios: turnos del mes (calendario) y patrón semanal con descansos por asesor. */
export function SchedulesTab({ companyId }: { companyId: string }) {
  const [view, setView] = useState<'mes' | 'patron'>('mes');
  return (
    <div className="space-y-3">
      <div className="bg-card inline-flex gap-1 rounded-md border p-1" data-testid="schedule-views">
        {(
          [
            ['mes', 'Turnos del mes', CalendarDays],
            ['patron', 'Patrón semanal', CalendarClock],
          ] as const
        ).map(([key, label, Icon]) => (
          <button
            key={key}
            type="button"
            onClick={() => setView(key)}
            className={`flex items-center gap-1 rounded px-3 py-1.5 text-sm ${view === key ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'}`}
          >
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>
      {view === 'mes' ? (
        <MonthShifts companyId={companyId} />
      ) : (
        <WeeklyPatterns companyId={companyId} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------- turnos del mes

const cellClass = (c: ScheduleCell) =>
  c.source === 'TURNO'
    ? 'text-primary font-medium'
    : c.source === 'DESCANSO'
      ? 'text-warning'
      : c.source === 'PATRON'
        ? 'text-muted-foreground'
        : c.source === 'SALA'
          ? 'text-muted-foreground/70 italic'
          : 'text-muted-foreground/50';

/** Texto de una celda del calendario: turno, libre, patrón, «Sala» (respaldo) o «—» (no atiende). */
const cellText = (c: ScheduleCell) =>
  c.source === 'NINGUNO'
    ? '—'
    : c.source === 'SALA'
      ? 'Sala'
      : c.source === 'DESCANSO'
        ? 'Libre'
        : c.ranges.map((r) => `${r.start}–${r.end}`).join(' ');

/** Lo mismo, en palabras, para el diálogo del día (misma regla que la celda). */
const cellSentence = (c: ScheduleCell) =>
  c.source === 'NINGUNO'
    ? 'no atiende (sin turno cargado ni patrón para ese día)'
    : c.source === 'SALA'
      ? 'usa el horario de la sala (sin patrón ni turnos cargados este mes)'
      : describeRanges(c.ranges);

/** Mensaje del horario si «Hasta» no es mayor que «Desde» (o falta una hora); null si está bien. */
const rangeError = (start: string, end: string): string | null => {
  if (!start || !end) return 'Indica la hora de inicio y la de fin';
  const r = timeRangeSchema.safeParse({ start, end });
  return r.success ? null : (r.error.issues[0]?.message ?? 'Horario inválido');
};

function FieldError({ id, message }: { id: string; message?: string | null }) {
  if (!message) return null;
  return (
    <p id={id} className="text-danger text-xs" role="alert">
      {message}
    </p>
  );
}

function MonthShifts({ companyId }: { companyId: string }) {
  const tz = useCompanyTimezone(companyId);
  const [month, setMonth] = useState(() => currentMonth(tz));
  const { data, isLoading } = useScheduleMonth(companyId, month);
  const copy = useCopyScheduleMonth();
  const clearMonth = useClearScheduleMonth();
  const toast = useToast();
  const [editing, setEditing] = useState<{
    userId: string;
    fullName: string;
    date: string;
    cell: ScheduleCell;
  } | null>(null);
  const [bulk, setBulk] = useState(false);
  const todayYmd = todayInTz(tz);

  // Copia por semana y día de la semana (Lote 6 · I2, P14): primero la vista previa, luego la confirmación con
  // cuántos días y asesores se copian y cuántos turnos pierde el mes destino.
  const copyPrevious = async () => {
    const from = shiftMonthKey(month, -1);
    try {
      const preview = await copy.mutateAsync({ companyId, from, to: month, dryRun: true });
      if (preview.days === 0) {
        toast.push({
          tone: 'info',
          title: `No hay turnos en ${monthLabel(from)} que copiar a ${monthLabel(month)}`,
          description:
            'La copia va por semana y día de la semana (el lunes de la primera semana al lunes de la primera semana).',
        });
        return;
      }
      const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
      if (
        !confirm(
          `Se copian ${plural(preview.days, 'día', 'días')} de ${plural(preview.advisors, 'asesor', 'asesores')} de ${monthLabel(from)} a ${monthLabel(month)}, por semana y día de la semana; ${monthLabel(month)} pierde ${plural(preview.lost, 'turno ya cargado', 'turnos ya cargados')}. ¿Continuar?`,
        )
      )
        return;
      const r = await copy.mutateAsync({ companyId, from, to: month });
      toast.success(
        `Turnos copiados: ${plural(r.days, 'día', 'días')} de ${plural(r.advisors, 'asesor', 'asesores')}${r.lost ? ` · ${monthLabel(month)} perdió ${plural(r.lost, 'turno', 'turnos')}` : ''}`,
      );
    } catch (err) {
      toast.error('No se pudo copiar', (err as Error).message);
    }
  };
  // Una sola llamada para todos los asesores (ux-equipo-27): o se limpia el mes entero o no se toca nada.
  const applyPattern = async () => {
    if (!data) return;
    if (
      !confirm(
        `¿Quitar todos los turnos cargados en ${monthLabel(month)}? Volverá a valer el patrón semanal de cada asesor.`,
      )
    )
      return;
    try {
      const r = await clearMonth.mutateAsync({ companyId, month });
      toast.success(
        r.days === 0
          ? 'El mes vuelve al patrón semanal (no había turnos cargados)'
          : `El mes vuelve al patrón semanal: ${r.days} turno${r.days === 1 ? '' : 's'} de ${r.advisors} asesor${r.advisors === 1 ? '' : 'es'}`,
      );
    } catch (err) {
      toast.error('No se pudo aplicar', (err as Error).message);
    }
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5" /> Turnos de {monthLabel(month)}
          </span>
          <span className="flex items-center gap-1">
            <Button
              size="icon"
              variant="ghost"
              aria-label="Mes anterior"
              onClick={() => setMonth(shiftMonthKey(month, -1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              aria-label="Mes siguiente"
              onClick={() => setMonth(shiftMonthKey(month, 1))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </span>
        </CardTitle>
        <CardDescription>
          Toca un día para ponerle horario o marcarlo libre. Lo cargado aquí manda sobre el patrón
          semanal; los días en blanco siguen el patrón y, si el asesor tiene turnos en el mes, un
          día sin turno ni patrón es «no atiende» (—). «Sala» = sin patrón ni turnos este mes: vale
          el horario de la sala. En azul los turnos del mes, en ámbar los días libres, en gris el
          patrón.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setBulk(true)}
            data-testid="btn-bulk-days"
          >
            <Plus className="h-4 w-4" /> Cargar varios días
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => void copyPrevious()}
            disabled={copy.isPending}
          >
            Copiar el mes anterior
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void applyPattern()}
            disabled={clearMonth.isPending}
          >
            Aplicar el patrón semanal
          </Button>
        </div>
        {isLoading || !data ? (
          <Skeleton className="h-48 w-full" />
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <table className="text-xs" data-testid="shift-grid">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr>
                  <th className="bg-muted/50 sticky left-0 z-10 px-2 py-1 text-left">Asesor</th>
                  {data.days.map((d) => {
                    const wd = weekdayOf(d);
                    return (
                      <th
                        key={d}
                        className={`px-1 py-1 text-center font-normal ${wd === 0 ? 'text-warning' : ''} ${d === todayYmd ? 'text-primary font-semibold' : ''}`}
                      >
                        <div>{Number(d.slice(8, 10))}</div>
                        <div className="text-[10px]">{DAY_SHORT[wd]}</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {data.items.map((it) => (
                  <tr key={it.userId} className="border-t" data-testid="shift-row">
                    <td className="bg-card sticky left-0 z-10 whitespace-nowrap px-2 py-1 font-medium">
                      {it.fullName}
                    </td>
                    {data.days.map((d) => {
                      const c = it.cells[d]!;
                      return (
                        <td key={d} className="p-0">
                          <button
                            type="button"
                            className={`hover:bg-accent h-12 w-[52px] px-0.5 text-center leading-tight ${cellClass(c)}`}
                            onClick={() =>
                              setEditing({
                                userId: it.userId,
                                fullName: it.fullName,
                                date: d,
                                cell: c,
                              })
                            }
                            data-testid="shift-cell"
                            data-user={it.userId}
                            data-date={d}
                            data-source={c.source}
                            title={
                              c.source === 'SALA'
                                ? `${it.fullName} · ${d} · Usa el horario de la sala`
                                : c.source === 'NINGUNO'
                                  ? `${it.fullName} · ${d} · No atiende`
                                  : `${it.fullName} · ${d}`
                            }
                          >
                            {cellText(c)}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
      {editing && (
        <DayDialog
          companyId={companyId}
          userId={editing.userId}
          fullName={editing.fullName}
          date={editing.date}
          cell={editing.cell}
          onClose={() => setEditing(null)}
        />
      )}
      {bulk && data && (
        <BulkDaysDialog
          companyId={companyId}
          month={month}
          people={data.items}
          onClose={() => setBulk(false)}
        />
      )}
    </Card>
  );
}

type DayMode = 'patron' | 'horario' | 'libre';

function DayFields({
  mode,
  setMode,
  start,
  end,
  setStart,
  setEnd,
  error,
}: {
  mode: DayMode;
  setMode: (m: DayMode) => void;
  start: string;
  end: string;
  setStart: (v: string) => void;
  setEnd: (v: string) => void;
  /** Error de validación del horario (fin no mayor que inicio, hora vacía), bajo los campos. */
  error?: string | null;
}) {
  return (
    <div className="space-y-2 text-sm">
      {(
        [
          ['horario', 'Atiende en este horario'],
          ['libre', 'Día libre'],
          ['patron', 'Según el patrón semanal'],
        ] as const
      ).map(([key, label]) => (
        <label key={key} className="flex items-center gap-2">
          <input
            type="radio"
            name="day-mode"
            checked={mode === key}
            onChange={() => setMode(key)}
          />
          {label}
        </label>
      ))}
      {mode === 'horario' && (
        <div className="space-y-1 pl-6">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label htmlFor="day-start">Desde</Label>
              <Input
                id="day-start"
                type="time"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                className="h-9"
                aria-invalid={!!error}
                aria-describedby={error ? 'day-range-error' : undefined}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="day-end">Hasta</Label>
              <Input
                id="day-end"
                type="time"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                className="h-9"
                aria-invalid={!!error}
                aria-describedby={error ? 'day-range-error' : undefined}
              />
            </div>
          </div>
          <FieldError id="day-range-error" message={error} />
        </div>
      )}
    </div>
  );
}

function DayDialog({
  companyId,
  userId,
  fullName,
  date,
  cell,
  onClose,
}: {
  companyId: string;
  userId: string;
  fullName: string;
  date: string;
  cell: ScheduleCell;
  onClose: () => void;
}) {
  const setDays = useSetScheduleDays();
  const toast = useToast();
  const [mode, setMode] = useState<DayMode>(
    cell.source === 'TURNO' ? 'horario' : cell.source === 'DESCANSO' ? 'libre' : 'patron',
  );
  const [start, setStart] = useState(cell.ranges[0]?.start ?? '09:00');
  const [end, setEnd] = useState(cell.ranges[0]?.end ?? '18:00');
  const [error, setError] = useState<string | null>(null);
  const label = `${DAY_NAMES[weekdayOf(date)]} ${Number(date.slice(8, 10))}`;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    // Validación previa con el mismo esquema que la API (P15): el error va bajo el campo, no en un aviso genérico.
    const invalid = mode === 'horario' ? rangeError(start, end) : null;
    setError(invalid);
    if (invalid) return;
    try {
      if (mode === 'patron')
        await setDays.mutateAsync({ companyId, userId, days: [{ date, ranges: [] }], clear: true });
      else
        await setDays.mutateAsync({
          companyId,
          userId,
          days: [{ date, ranges: mode === 'libre' ? [] : [{ start, end }] }],
        });
      toast.success('Turno guardado', `${fullName} · ${label}`);
      onClose();
    } catch (err) {
      toast.error('No se pudo guardar', (err as Error).message);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <form onSubmit={(e) => void submit(e)} className="space-y-4">
          <DialogHeader>
            <DialogTitle>
              {fullName} · {label}
            </DialogTitle>
            <DialogDescription>Ese día vale: {cellSentence(cell)}.</DialogDescription>
          </DialogHeader>
          <DayFields
            mode={mode}
            setMode={setMode}
            start={start}
            end={end}
            setStart={(v) => {
              setStart(v);
              setError(null);
            }}
            setEnd={(v) => {
              setEnd(v);
              setError(null);
            }}
            error={error}
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={setDays.isPending} data-testid="btn-save-day">
              Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BulkDaysDialog({
  companyId,
  month,
  people,
  onClose,
}: {
  companyId: string;
  month: string;
  people: Array<{ userId: string; fullName: string }>;
  onClose: () => void;
}) {
  const setDays = useSetScheduleDays();
  const toast = useToast();
  const days = monthDays(month);
  const [userId, setUserId] = useState(people[0]?.userId ?? '');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState<DayMode>('horario');
  const [start, setStart] = useState('09:00');
  const [end, setEnd] = useState('18:00');
  const [error, setError] = useState<string | null>(null);
  const toggle = (d: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(d)) n.delete(d);
      else n.add(d);
      return n;
    });
  const pickWeekday = (wd: number) =>
    setSelected((s) => {
      const n = new Set(s);
      for (const d of days) if (weekdayOf(d) === wd) n.add(d);
      return n;
    });
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!userId || selected.size === 0) return;
    const invalid = mode === 'horario' ? rangeError(start, end) : null;
    setError(invalid);
    if (invalid) return;
    const list = [...selected].sort().map((date) => ({
      date,
      ranges: mode === 'libre' || mode === 'patron' ? [] : [{ start, end }],
    }));
    try {
      await setDays.mutateAsync({ companyId, userId, days: list, clear: mode === 'patron' });
      toast.success(
        `${list.length} día${list.length === 1 ? '' : 's'} cargado${list.length === 1 ? '' : 's'}`,
      );
      onClose();
    } catch (err) {
      toast.error('No se pudo guardar', (err as Error).message);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <form onSubmit={(e) => void submit(e)} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Cargar varios días · {monthLabel(month)}</DialogTitle>
            <DialogDescription>
              Elige al asesor, marca los días y define qué vale en todos ellos.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <Label htmlFor="bulk-advisor">Asesor</Label>
            <Select id="bulk-advisor" value={userId} onChange={(e) => setUserId(e.target.value)}>
              {people.map((p) => (
                <option key={p.userId} value={p.userId}>
                  {p.fullName}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex flex-wrap gap-1 text-xs">
            <span className="text-muted-foreground self-center">Marcar todos los:</span>
            {ORDER.map((wd) => (
              <Button
                key={wd}
                type="button"
                size="sm"
                variant="outline"
                className="h-7"
                onClick={() => pickWeekday(wd)}
              >
                {DAY_SHORT[wd]}
              </Button>
            ))}
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7"
              onClick={() => setSelected(new Set())}
            >
              Ninguno
            </Button>
          </div>
          <div className="grid grid-cols-7 gap-1" data-testid="bulk-days">
            {days.map((d) => {
              const on = selected.has(d);
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => toggle(d)}
                  aria-pressed={on}
                  aria-label={`Día ${Number(d.slice(8, 10))}`}
                  className={`rounded border px-1 py-1 text-xs ${on ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'} ${weekdayOf(d) === 0 && !on ? 'text-warning' : ''}`}
                >
                  {Number(d.slice(8, 10))} {DAY_SHORT[weekdayOf(d)]}
                </button>
              );
            })}
          </div>
          <DayFields
            mode={mode}
            setMode={setMode}
            start={start}
            end={end}
            setStart={(v) => {
              setStart(v);
              setError(null);
            }}
            setEnd={(v) => {
              setEnd(v);
              setError(null);
            }}
            error={error}
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={!userId || selected.size === 0 || setDays.isPending}
              data-testid="btn-save-bulk"
            >
              Guardar {selected.size > 0 ? `(${selected.size})` : ''}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- patrón semanal

function WeeklyPatterns({ companyId }: { companyId: string }) {
  const { data, isLoading } = useSchedules(companyId);
  const tz = useCompanyTimezone(companyId);
  const [editing, setEditing] = useState<AdvisorScheduleDto | null>(null);
  if (isLoading || !data) return <Skeleton className="h-64 w-full" />;
  // «Hoy» en la zona de la empresa: desde las 19:00 de Lima, UTC ya daba por pasado un descanso que termina hoy.
  const today = todayInTz(tz);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarClock className="h-5 w-5" /> Patrón semanal
        </CardTitle>
        <CardDescription>
          Días y horas en que cada asesor atiende normalmente, más sus descansos. Vale para los días
          sin turno cargado en el mes. Sin patrón ni turnos en el mes, se usa el horario de la sala
          de ventas; con turnos cargados, los días en blanco son «no atiende».
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground text-left text-xs uppercase">
              <tr>
                <th className="px-3 py-2">Asesor</th>
                <th className="px-3 py-2">Horario</th>
                <th className="px-3 py-2">Descansos</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((r) => {
                // Descansos vigentes o futuros, ordenados por fecha: el primero es el más cercano.
                const upcoming = r.exceptions
                  .filter((e) => e.to >= today)
                  .sort((a, b) => a.from.localeCompare(b.from));
                return (
                  <tr key={r.userId} className="border-t" data-testid="schedule-row">
                    <td className="px-3 py-2">
                      <p className="font-medium">{r.fullName}</p>
                      <p className="text-muted-foreground text-xs">{ROLE[r.role] ?? r.role}</p>
                    </td>
                    <td className="px-3 py-2">
                      {hasSchedule(r.weekly) ? (
                        describeWeekly(r.weekly)
                      ) : hasOverridesInMonth(r.overrides, today) ? (
                        <Badge
                          tone="muted"
                          title="Sin patrón semanal: este mes solo atiende los días con turno cargado"
                        >
                          Sin patrón · este mes solo los turnos cargados
                        </Badge>
                      ) : (
                        <Badge
                          tone="muted"
                          title="Sin patrón ni turnos este mes: vale el horario de la sala"
                        >
                          Sin horario · usa el de la sala
                        </Badge>
                      )}
                    </td>
                    <td className="text-muted-foreground px-3 py-2 text-xs">
                      {upcoming.length === 0
                        ? '—'
                        : `${upcoming.length} descanso${upcoming.length === 1 ? '' : 's'} · ${upcoming[0]!.from <= today ? 'en curso hasta' : 'próximo'} ${fmt.date(upcoming[0]!.from <= today ? upcoming[0]!.to : upcoming[0]!.from)}${upcoming[0]!.reason ? ` (${upcoming[0]!.reason})` : ''}`}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditing(r)}
                        aria-label="Editar horario"
                      >
                        Editar horario
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {data.items.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-muted-foreground px-3 py-8 text-center">
                    No hay asesores en esta empresa.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
      {editing && (
        <ScheduleDialog companyId={companyId} row={editing} onClose={() => setEditing(null)} />
      )}
    </Card>
  );
}

type DayForm = { on: boolean; start: string; end: string };
const toForm = (weekly: WeeklySchedule): Record<number, DayForm> =>
  Object.fromEntries(
    ORDER.map((d) => {
      const r = weekly[String(d)]?.[0];
      return [d, { on: !!r, start: r?.start ?? '09:00', end: r?.end ?? '18:00' }];
    }),
  ) as Record<number, DayForm>;

function ScheduleDialog({
  companyId,
  row,
  onClose,
}: {
  companyId: string;
  row: AdvisorScheduleDto;
  onClose: () => void;
}) {
  const { data: catalogs } = useCatalogs(companyId);
  const upsert = useUpsertSchedule();
  const toast = useToast();
  const [days, setDays] = useState<Record<number, DayForm>>(() => toForm(row.weekly));
  const [exceptions, setExceptions] = useState<ScheduleException[]>(row.exceptions);
  // Errores por campo (`day-1`, `exc-0`) de la validación previa con los esquemas compartidos (P15).
  const [errors, setErrors] = useState<Record<string, string>>({});
  const setDay = (d: number, patch: Partial<DayForm>) => {
    setDays((x) => ({ ...x, [d]: { ...x[d]!, ...patch } }));
    setErrors((e) => {
      if (!(`day-${d}` in e)) return e;
      const { [`day-${d}`]: _gone, ...rest } = e;
      return rest;
    });
  };
  const setException = (i: number, patch: Partial<ScheduleException>) => {
    setExceptions(exceptions.map((y, j) => (j === i ? { ...y, ...patch } : y)));
    setErrors((e) => {
      if (!(`exc-${i}` in e)) return e;
      const { [`exc-${i}`]: _gone, ...rest } = e;
      return rest;
    });
  };
  const validate = () => {
    const next: Record<string, string> = {};
    for (const d of ORDER) {
      if (!days[d]!.on) continue;
      const err = rangeError(days[d]!.start, days[d]!.end);
      if (err) next[`day-${d}`] = err;
    }
    exceptions.forEach((x, i) => {
      if (!x.from && !x.to) return;
      const r = scheduleExceptionSchema.safeParse(x);
      if (!r.success) next[`exc-${i}`] = r.error.issues[0]?.message ?? 'Descanso inválido';
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    const weekly: WeeklySchedule = {};
    for (const d of ORDER)
      if (days[d]!.on) weekly[String(d)] = [{ start: days[d]!.start, end: days[d]!.end }];
    try {
      await upsert.mutateAsync({
        companyId,
        userId: row.userId,
        weekly,
        exceptions: exceptions.filter((x) => x.from && x.to),
      });
      toast.success('Horario guardado', row.fullName);
      onClose();
    } catch (err) {
      toast.error('No se pudo guardar', (err as Error).message);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <form onSubmit={(e) => void submit(e)} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Horario de {row.fullName}</DialogTitle>
            <DialogDescription>
              Marca los días en que atiende y el rango de horas. Los descansos son días completos en
              los que no se le agenda nada. Los turnos del mes ya cargados se conservan.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-2">
            {catalogs && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setDays(toForm(weeklyFromHours(catalogs.settings.businessHours)))}
              >
                Copiar el horario de la sala
              </Button>
            )}
            <Button type="button" size="sm" variant="ghost" onClick={() => setDays(toForm({}))}>
              Quitar todo
            </Button>
          </div>
          <div className="space-y-1.5" data-testid="schedule-days">
            {ORDER.map((d) => {
              const name = DAY_NAMES[d]!;
              const low = name.toLowerCase();
              const err = errors[`day-${d}`];
              return (
                <div key={d} className="space-y-1">
                  <div className="grid grid-cols-[130px_1fr_1fr] items-center gap-2 text-sm">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={days[d]!.on}
                        onChange={(e) => setDay(d, { on: e.target.checked })}
                        aria-label={name}
                      />
                      {name}
                    </label>
                    <Input
                      type="time"
                      value={days[d]!.start}
                      onChange={(e) => setDay(d, { start: e.target.value })}
                      disabled={!days[d]!.on}
                      aria-label={`Inicio ${low}`}
                      aria-invalid={!!err}
                      aria-describedby={err ? `day-error-${d}` : undefined}
                      className="h-9"
                    />
                    <Input
                      type="time"
                      value={days[d]!.end}
                      onChange={(e) => setDay(d, { end: e.target.value })}
                      disabled={!days[d]!.on}
                      aria-label={`Fin ${low}`}
                      aria-invalid={!!err}
                      aria-describedby={err ? `day-error-${d}` : undefined}
                      className="h-9"
                    />
                  </div>
                  {err && (
                    <div className="pl-[138px]">
                      <FieldError id={`day-error-${d}`} message={`${name}: ${err}`} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="space-y-2">
            <p className="text-sm font-medium">Descansos</p>
            {exceptions.map((x, i) => {
              const err = errors[`exc-${i}`];
              return (
                <div key={i} className="space-y-1" data-testid="schedule-exception">
                  <div className="grid gap-2 sm:grid-cols-[1fr_1fr_1fr_auto]">
                    <div className="space-y-1">
                      {i === 0 && <Label htmlFor={`exc-from-${i}`}>Desde</Label>}
                      <Input
                        id={`exc-from-${i}`}
                        type="date"
                        value={x.from}
                        onChange={(e) => setException(i, { from: e.target.value })}
                        aria-label={i === 0 ? undefined : `Desde ${i + 1}`}
                        aria-invalid={!!err}
                        aria-describedby={err ? `exc-error-${i}` : undefined}
                        className="h-9"
                        required
                      />
                    </div>
                    <div className="space-y-1">
                      {i === 0 && <Label htmlFor={`exc-to-${i}`}>Hasta</Label>}
                      <Input
                        id={`exc-to-${i}`}
                        type="date"
                        value={x.to}
                        onChange={(e) => setException(i, { to: e.target.value })}
                        aria-label={i === 0 ? undefined : `Hasta ${i + 1}`}
                        aria-invalid={!!err}
                        aria-describedby={err ? `exc-error-${i}` : undefined}
                        className="h-9"
                        required
                      />
                    </div>
                    <div className="space-y-1">
                      {i === 0 && <Label htmlFor={`exc-reason-${i}`}>Motivo</Label>}
                      <Input
                        id={`exc-reason-${i}`}
                        value={x.reason ?? ''}
                        onChange={(e) => setException(i, { reason: e.target.value })}
                        placeholder="Vacaciones, capacitación…"
                        aria-label={i === 0 ? undefined : `Motivo ${i + 1}`}
                        className="h-9"
                        maxLength={120}
                      />
                    </div>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="self-end"
                      aria-label={`Quitar descanso ${i + 1}`}
                      onClick={() => {
                        setExceptions(exceptions.filter((_, j) => j !== i));
                        setErrors({});
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <FieldError id={`exc-error-${i}`} message={err} />
                </div>
              );
            })}
            {exceptions.length < 50 && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setExceptions([...exceptions, { from: '', to: '', reason: null }])}
              >
                <Plus className="h-4 w-4" /> Agregar descanso
              </Button>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={upsert.isPending}>
              Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
